import React, { useEffect, useRef } from 'react';
import { Animated, Easing, Pressable, View, Modal, Image } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { Confetti } from '@components/Tutorialization/Confetti';
import theme from '@themes/theme';
import { widthPixel, heightPixel } from '@utils/normalise';

import { successCheckGif } from '../../../assets/Tutorials/Project';

export interface SuccessModalProps {
  title: string;
  description: string;
  pillList?: string[];
  primaryLabel: string;
  onPrimary: () => void;
  onDismiss?: () => void;
  showConfetti?: boolean;
}

export function SuccessModal({
  title,
  description,
  primaryLabel,
  onPrimary,
  pillList,
  onDismiss,
  showConfetti = false,
}: Readonly<SuccessModalProps>) {
  const backdropOpacity = useRef(new Animated.Value(0)).current;
  const cardScale = useRef(new Animated.Value(0.88)).current;
  const cardOpacity = useRef(new Animated.Value(0)).current;
  const actionsAnim = useRef(new Animated.Value(0)).current;
  const actionsTranslateY = actionsAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [10, 0],
  });

  const insets = useSafeAreaInsets();

  useEffect(() => {
    Animated.parallel([
      Animated.timing(backdropOpacity, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.spring(cardScale, {
        toValue: 1,
        damping: 20,
        stiffness: 260,
        useNativeDriver: true,
      }),
      Animated.timing(cardOpacity, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }),
      // CTA appears shortly after checkmark finishes (40ms delay + 220ms draw = 260ms)
      Animated.timing(actionsAnim, {
        toValue: 1,
        duration: 150,
        delay: 300,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
    ]).start();
  }, []);

  const pillRow = (pill: string, needBorder: boolean) => (
    <Box
      flexDirection='row'
      py='s'
      alignItems='center'
      alignSelf='stretch'
      justifyContent='flex-start'
      borderBottomColor={needBorder ? 'grey02' : 'transparent'}
      borderBottomWidth={needBorder ? 1 : 0}>
      <View
        style={{
          width: 32,
          height: 32,
          borderRadius: theme.spacing.m,
          backgroundColor: theme.colors.grey02,
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Icon
          name={pill.includes('@') ? 'Mail' : 'MoreHorizontal'}
          variant='m'
          color='grey04'></Icon>
      </View>
      <Text
        variant='bodySecondary'
        color='textPrimary'
        ml='xs'
        numberOfLines={1}
        ellipsizeMode='tail'
        style={{ flexShrink: 1 }}>
        {pill}
      </Text>
    </Box>
  );

  return (
    <Modal visible transparent animationType='none' onRequestClose={onDismiss}>
      <Confetti visible={showConfetti} />
      {/* Backdrop */}
      <Pressable
        testID='success-modal-backdrop'
        onPress={onDismiss}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 50,
        }}>
        <Animated.View
          style={
            {
              flex: 1,
              backgroundColor: 'rgba(0,0,0,0.45)',
              opacity: backdropOpacity,
            } as any
          }
        />
      </Pressable>

      {/* Card */}
      <Animated.View
        style={
          {
            position: 'absolute',
            bottom: 40 + insets.bottom,
            left: 16,
            right: 16,
            backgroundColor: theme.colors.white,
            borderRadius: 20,
            paddingTop: 28,
            paddingBottom: 28,
            overflow: 'hidden',
            zIndex: 60,
            opacity: cardOpacity,
            transform: [{ scale: cardScale }],
          } as any
        }>
        {/* Animated SVG checkmark */}
        <View style={{ alignItems: 'center' }}>
          <Image
            source={successCheckGif}
            style={{
              width: widthPixel(150),
              height: heightPixel(150),
            }}
            resizeMode='contain'
          />
        </View>

        {/* Title + description */}
        <Box px='l' mt='l' gap='s' alignItems='center'>
          <Text variant='heading2' color='textPrimary' textAlign='center'>
            {title}
          </Text>

          {pillList && pillList.length > 0 && (
            <View
              testID='success-modal-pill-list'
              style={
                {
                  alignItems: 'flex-start',
                  backgroundColor: theme.colors.grey01,
                  borderRadius: 10,
                  paddingHorizontal: 16,
                  // Size to content and center, rather than stretch full
                  // width — a single short email left a lot of empty grey
                  // space (TA-10919 #10). Cap the width so a very long
                  // address still truncates instead of forcing the box wide.
                  alignSelf: 'center',
                  maxWidth: widthPixel(280),
                } as any
              }>
              {(pillList.length > 3 ? pillList.slice(0, 2) : pillList).map(
                (pill, index) => {
                  return pillRow(pill, index < pillList.length - 1);
                }
              )}
              {pillList.length > 3 &&
                pillRow(
                  '+' + (pillList.length - 2).toString() + ' other',
                  false
                )}
            </View>
          )}

          <Text variant='body1' color='grey04' textAlign='center'>
            {description}
          </Text>
        </Box>

        {/* CTA buttons — fade + slide up after checkmark */}
        <Animated.View
          style={
            {
              marginHorizontal: theme.spacing.l,
              marginTop: theme.spacing.l,
              gap: theme.spacing.xs,
              opacity: actionsAnim,
              transform: [{ translateY: actionsTranslateY }],
            } as any
          }>
          <Pressable
            onPress={onPrimary}
            style={{
              backgroundColor: theme.colors.black,
              borderRadius: theme.borderRadii.xs,
              paddingVertical: 14,
              alignItems: 'center',
            }}>
            <Text variant='webSmall' color='white'>
              {primaryLabel}
            </Text>
          </Pressable>
        </Animated.View>
      </Animated.View>
    </Modal>
  );
}
